import { useCurrentStateAndParams } from "@uirouter/react";
import purify from "dompurify";

import { Trans, useTranslate } from "@similarweb/i18n-provider";
import { Flex } from "@similarweb/ui-components/foundations";

import type { ICampaignDiscountBannerConfig } from "app/components/discountCampaign/campaignScenarioConfig.types";
import { PAGE_TOP_DISCOUNT_BANNER_DOM_ID } from "app/components/PageTopBanner/DiscountPageTopBanner/discountPageTopBannerConstants";
import { allTrackers } from "app/services/track/track";

import {
    CloseButton,
    DiscountBannerContent,
    DiscountBannerContentGroup,
    DiscountBannerCoupon,
    DiscountBannerCtaButton,
    DiscountBannerSuffixWithCounter,
    DiscountBannerText,
    DiscountBannerTitle,
} from "./StyledDiscountBanner";
import { TimeCountingDiscount } from "./TimeCountingDiscount/TimeCountingDiscount";
import { getFormattedTimeLeft } from "./utils/helpers";

const DEFAULT_BANNER_DISCOUNT_PERCENTAGE = 20;
const STICKY_PAGES = ["activation", "track"];

export interface IDiscountBannerProps {
    bannerConfig: ICampaignDiscountBannerConfig;
    discountEndTime: string;
    onCtaClick: () => void;
    onClose?: () => void;
}

export const DiscountBanner = ({ bannerConfig, discountEndTime, onCtaClick, onClose }: IDiscountBannerProps) => {
    const { translate } = useTranslate();
    const { theme, content } = bannerConfig;
    const {
        state: { parent },
    } = useCurrentStateAndParams();
    const isSticky = typeof parent === "string" && STICKY_PAGES.includes(parent);
    const BannerIcon = content.icon;

    const handleCTAClick = () => {
        const trackingEventName = content.trackingEventName ?? "TrialBanner/Discount Banner";
        const trackingLabel = theme.showTimer
            ? `${trackingEventName}/${getFormattedTimeLeft({ discountEndTime })}`
            : trackingEventName;

        allTrackers.dispatchTrackEvent("Internal Link", "click", trackingLabel);
        onCtaClick();
    };

    return (
        <DiscountBannerContent
            isSticky={isSticky}
            bannerTheme={theme}
            hasButton={theme.showButton}
            id={PAGE_TOP_DISCOUNT_BANNER_DOM_ID}
            data-automation="campaign-discount-banner"
        >
            <DiscountBannerContentGroup $hasButton={theme.showButton}>
                {BannerIcon ? (
                    <BannerIcon
                        size={content.iconSize}
                        color={content.iconColor}
                    />
                ) : null}
                {content.title ? (
                    <DiscountBannerTitle
                        dangerouslySetInnerHTML={{
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            __html: purify.sanitize(translate(content.title)),
                        }}
                    />
                ) : null}

                <Flex
                    justifyContent="center"
                    alignItems="center"
                    gap="4px"
                >
                    {content.text ? (
                        <DiscountBannerText
                            dangerouslySetInnerHTML={{
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                __html: purify.sanitize(
                                    translate(content.text, {
                                        percentage: content.percentage ?? DEFAULT_BANNER_DISCOUNT_PERCENTAGE,
                                    }),
                                ),
                            }}
                        />
                    ) : null}

                    {theme.showTimer ? (
                        <DiscountBannerSuffixWithCounter>
                            <DiscountBannerText>- {translate("pro.time.counting.discount.title.1")}</DiscountBannerText>
                            <TimeCountingDiscount
                                discountEndTime={discountEndTime}
                                bannerTheme={theme}
                            />
                        </DiscountBannerSuffixWithCounter>
                    ) : null}
                </Flex>
            </DiscountBannerContentGroup>
            <Flex
                gap="16px"
                alignItems="center"
            >
                {content.couponCode ? (
                    <DiscountBannerCoupon>
                        <Trans
                            i18nKey={translate("campaign.code", {
                                code: Array.isArray(content.couponCode) ? content.couponCode[0] : content.couponCode,
                            })}
                        />
                    </DiscountBannerCoupon>
                ) : null}

                {theme.showButton ? (
                    <DiscountBannerCtaButton
                        $width={136}
                        $bannerTheme={theme}
                        onClick={handleCTAClick}
                    >
                        {translate(content.buttonText ?? "discount.cta.banner.button")}
                    </DiscountBannerCtaButton>
                ) : null}
                {theme.showCloseButton ? (
                    <CloseButton
                        type="flat"
                        iconName="close"
                        iconSize="xs"
                        iconColor={theme.closeButtonColor ?? theme.color}
                        onClick={onClose}
                    />
                ) : null}
            </Flex>
        </DiscountBannerContent>
    );
};
